import { Grid, Typography } from '@mui/material';
import { FieldErrors, FormProvider, useForm, useFormState } from 'react-hook-form';
import { createInvoiceDefault, CreateInvoiceForm, createInvoiceResolver } from './helpers/createInvoiceSchema';
import InvoiceCategoryTypeIdField from './components/InvoiceCategoryTypeIdField';
import InvoiceTaxableField from './components/InvoiceTaxableField/InvoiceTaxableField';
import InvoiceAmountField from './components/InvoiceAmountField/InvoiceAmountField';
import InvoiceTotalField from './components/InvoiceTotalField';
import InvoiceScheduledDateField from './components/InvoiceScheduledDateField/InvoiceScheduledDateField';
import InvoiceNotesField from './components/InvoiceNotesField';
import { useGetIds } from '~/modules/auth';
import { useCreateInvoiceMutation } from '~/services/member';
import { errorReporting } from '~/services/logging';
import { useEffect, useMemo } from 'react';
import { useGetMembersByLocationQuery } from '~/services/member';
import { toast } from 'sonner';
import { ContactNameAutocomplete, ContactType } from '~/modules/@shared/components';
import MemberPaymentMethod from '~/modules/payment/components/TakePayment/components/TakePaymentModal/components/TakePaymentForm/components/MemberPaymentMethod.tsx/MemberPaymentMethod';
import { useMemberPaymentMethods } from '~/modules/contact/hooks/useMemberPaymentMethods';
import usePaymentMethodsStore from '../../../contact/components/ContactDetails/components/TabsContainer/components/MembershipTab/components/PaymentMethodsSection/components/PaymentMethods/hooks/usePaymentMethodsStore';
import { useLocationContext } from '~/contexts/location.context';
import { Modal } from '~/modules/@shared/components/ModalTemplate';
import useModalTemplateStore from '~/modules/@shared/components/ModalTemplate/hooks/useModalTemplateStore';

export default function CreateInvoiceModal() {
  const { locationId, memberId: defaultMemberId } = useGetIds();
  const { isDea } = useLocationContext();
  const isOpen = useModalTemplateStore((state) => state.isOpen);
  const close = useModalTemplateStore((state) => state.close);

  const setMemberId = usePaymentMethodsStore((state) => state.setMemberId);
  const openPaymentMethodsModal = usePaymentMethodsStore((state) => state.open);

  const { data: rawAllMembers } = useGetMembersByLocationQuery({ locationId, registered: 0 });

  const { mutateAsync: createInvoice, isPending: isCreatingInvoice } = useCreateInvoiceMutation({
    locationId,
  });

  const methods = useForm<CreateInvoiceForm>({
    mode: 'onChange',
    defaultValues: { ...createInvoiceDefault, member_id: defaultMemberId },
    resolver: createInvoiceResolver,
  });

  const { errors } = useFormState({ control: methods.control, name: 'member_id' });

  const { watch, handleSubmit, trigger, reset, setValue } = methods;

  const member_id = watch('member_id');

  const { paymentMethods } = useMemberPaymentMethods({ locationId, memberId: member_id, isDea });

  const handleOpenPaymentMethodsModal = () => {
    if (!member_id) return;

    setMemberId(member_id);
    openPaymentMethodsModal();
  };

  const handleSelectMember = (option: ContactType) => {
    setValue('member_id', option.id);
  };

  const onSubmit = async (data: CreateInvoiceForm) => {
    const { taxable, member_id, ...dataWithoutTaxable } = data;

    if (!member_id) {
      toast.error('Please select a member to continue');
      return;
    }

    const defaultPaymentMethod = paymentMethods?.find((paymentMethod) => paymentMethod.default)?.id;

    try {
      const response = await createInvoice({
        member_id,
        ...dataWithoutTaxable,
        auto_payment_on_due: data.payment_method_id === 'automatic_charge',
        payment_method_id:
          // we can safety set defaultPaymentMethod with the ! because if the user selected automatic charge, it means that the default payment method is set
          data.payment_method_id === 'automatic_charge' ? defaultPaymentMethod! : data.payment_method_id,
      });

      if (response.status === 'CREATED') {
        reset(createInvoiceDefault);
        close();
      }
    } catch (error) {
      errorReporting.logError(error as Error);
      toast.error('Failed to create charge. Please try again.');
    } finally {
      reset(createInvoiceDefault);
    }
  };

  const handleError = (error: FieldErrors<CreateInvoiceForm>) => {
    errorReporting.logError(new Error(JSON.stringify(error)));
  };

  const memberName = useMemo(() => {
    const selectedMember = (rawAllMembers || []).find((member) => member.member_id === member_id);

    if (selectedMember) {
      return `${selectedMember.first_name} ${selectedMember.last_name}`;
    }
    return '';
  }, [member_id, rawAllMembers]);

  useEffect(() => {
    if (!isOpen) {
      reset(createInvoiceDefault);
    }
  }, [isOpen, reset]);

  return (
    <Modal.Modal title="Create Charge">
      <FormProvider {...methods}>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <Grid item xs={12} sm={7}>
            <Typography variant="h3">Contact Name</Typography>
          </Grid>

          <Grid item xs={12} sm={5}>
            <ContactNameAutocomplete
              defaultOption={member_id ? { id: member_id, name: memberName } : null}
              onSelect={(option) => handleSelectMember(option)}
              shouldLoadAllMembers={true}
              error={errors.member_id?.message || ''}
            />
          </Grid>

          <Grid item xs={12} sm={7}>
            <Typography variant="h3">Category</Typography>
          </Grid>
          <Grid item xs={12} sm={5}>
            <InvoiceCategoryTypeIdField />
          </Grid>

          <Grid item xs={12}>
            <InvoiceTaxableField />
          </Grid>

          <Grid item xs={12} sm={8}>
            <Typography variant="h3">Charged Amount</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <InvoiceAmountField />
          </Grid>

          <Grid item xs={12} sm={8}>
            <Typography variant="h3">Total</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <InvoiceTotalField />
          </Grid>

          <Grid item xs={12} sm={8}>
            <Typography variant="h3">Due Date</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <InvoiceScheduledDateField />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="h3">
              Description
              <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                (optional)
              </Typography>
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', ml: 0.5, display: 'flex', justifyContent: 'right' }}>
              {watch('notes')?.length} / 250
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <InvoiceNotesField />
          </Grid>
          <Grid item xs={4} sm={4}>
            <Typography variant="h3">Payment method</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <MemberPaymentMethod
              paymentMethods={paymentMethods}
              onOpenPaymentMethodsModal={handleOpenPaymentMethodsModal}
              addAutomaticPaymentOnDueDateOption={true}
            />
          </Grid>
        </Grid>
      </FormProvider>
      <Modal.CancelButton />
      <Modal.ActionButton
        isDisabled={!member_id}
        label="Create Charge & Process Payment"
        isLoading={isCreatingInvoice}
        onClick={() => {
          void trigger();
          void handleSubmit(onSubmit, handleError)();
        }}
      />
    </Modal.Modal>
  );
}
